import { ChangeEvent, createContext, Dispatch, JSX, ReactNode, SetStateAction, useEffect, useState } from "react";
import { ExportService } from "../exports";
import { authenticatedDownload } from "../shared/AuthenticatedDownload";
import { convertDateTime } from "../shared/convertDateTime";
import { AnalysisTypeConverter, AvailableFilterCriteria, ReportsUIModel, ExportTypeConverter, ProjectTypeConverter, ProviderCategoryConverter, ReportFileTypeConverter, ReportStatusConverter, ServiceAreaTypeConverter, SpecialtyFilterConverter } from "./reportsModel";
import { QESButton } from "@app/shared/qes-standards";

interface ReportsProps {
  availableFilterCriteria: AvailableFilterCriteria;
  handleSearch: () => void;
  handleSearchTypeChange: (e: ChangeEvent<HTMLSelectElement>) => void;
  searching: boolean;
  searchType: string;
  searchValue: string;
  searchValue2: string;

  hasLoadedToday: boolean;
  hasLoadedLast7: boolean;
  hasLoadedLast30: boolean;
  hasLoadedSharedToday: boolean;
  hasLoadedSharedLast7: boolean;
  hasLoadedSharedLast30: boolean;

  reportsToday: ReportsUIModel[];
  reportsLast7: ReportsUIModel[];
  reportsLast30: ReportsUIModel[];
  sharedReportsToday: ReportsUIModel[];
  sharedReportsLast7: ReportsUIModel[];
  sharedReportsLast30: ReportsUIModel[];

  setSearchType: Dispatch<SetStateAction<string>>;
  setSearchValue: Dispatch<SetStateAction<string>>;
  setSearchValue2: Dispatch<SetStateAction<string>>;

  fetchToday: () => Promise<void>;
  fetchLast7: () => Promise<void>;
  fetchLast30: () => Promise<void>;
  fetchSharedToday: () => Promise<void>;
  fetchSharedLast7: () => Promise<void>;
  fetchSharedLast30: () => Promise<void>;

  downloadFile: (projectId: number, reportId: number) => void;
}

export const ReportsContext = createContext<Partial<ReportsProps>>({});

const formatBoolean = (boolean: boolean) => {
  return boolean ? "Yes" : "No";
}

const reportStatusIcons: { [key: string]: JSX.Element } = {
  "Complete": <i className="fa fa-check-circle text-success" />,
  "Failed": <i className="fa fa-times-circle text-danger" />,
  "No Data Found": <i className="fa fa-times-circle text-danger" />,
  "Preview": <i className="fa fa-eye text-danger" />,
  "Purged": <i className="fa fa-times-circle text-danger" />,
  "Stale": <i className="fa fa-exclamation-circle text-warning" />,
  "Vintage": <i className="fa fa-exclamation-circle text-warning" />,
}

const exportService = new ExportService();
let searchDebounceTimeout: number;
export const ReportsContextProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [searchType, setSearchType] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [searchValue2, setSearchValue2] = useState("");
  const [searching, setSearching] = useState(false);

  const [hasLoadedToday, setHasLoadedToday] = useState(false);
  const [hasLoadedLast7, setHasLoadedLast7] = useState(false);
  const [hasLoadedLast30, setHasLoadedLast30] = useState(false);
  const [hasLoadedSharedToday, setHasLoadedSharedToday] = useState(false);
  const [hasLoadedSharedLast7, setHasLoadedSharedLast7] = useState(false);
  const [hasLoadedSharedLast30, setHasLoadedSharedLast30] = useState(false);

  const [reportsToday, setReportsToday] = useState<ReportsUIModel[]>([]);
  const [reportsLast7, setReportsLast7] = useState<ReportsUIModel[]>([]);
  const [reportsLast30, setReportsLast30] = useState<ReportsUIModel[]>([]);
  const [sharedReportsToday, setSharedReportsToday] = useState<ReportsUIModel[]>([]);
  const [sharedReportsLast7, setSharedReportsLast7] = useState<ReportsUIModel[]>([]);
  const [sharedReportsLast30, setSharedReportsLast30] = useState<ReportsUIModel[]>([]);
  const [availableFilterCriteria, setAvailableFilterCriteria] = useState<AvailableFilterCriteria>();

  useEffect(() => {
    fetchToday();
  }, []);

  useEffect(() => {
    const todayDate = new Date();
    todayDate.setHours(24, 0, 0, 0);

    const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
    thirtyDaysAgo.setHours(0, 0, 0, 0);

    exportService.getAvailableFilterCriteria(thirtyDaysAgo.toUTCString(), todayDate.toUTCString()).then((response) => setAvailableFilterCriteria(response));
  }, []);

  useEffect(() => {
    window.clearTimeout(searchDebounceTimeout);
    searchDebounceTimeout = window.setTimeout(() => {
      if (searchType === "As Of" || searchType === "Last Requested") {
        if (!isNaN(new Date(searchValue).getTime()) && !isNaN(new Date(searchValue2).getTime())) {
          handleSearch();
        }
      } else {
        handleSearch();
      }
    }, 400);
  }, [searchValue, searchValue2]);

  const makeDownloadButton = (projectId: number, reportId: number, reportName: string) => {
    return (
      <QESButton
        qesType="secondary"
        onClick={(e) => { e.preventDefault(); downloadFile(projectId, reportId); }}
        className="download-button"
        qesTestId = "report-context-download-button"
      >
        {reportName}
      </QESButton>
    )
  }

  const downloadFile = (projectId: number, reportId: number) => {
    const link = (!!projectId && projectId > 0) ? `${exportService.getProjectReportLink(projectId, reportId)}` : `${exportService.getReportLink(reportId)}`;
    authenticatedDownload(link);
  }

  const fixResponse = (dl: ReportsUIModel) => {

    const asOfTimestamp = convertDateTime(dl.asOfUtc);
    const lastRequestTimestamp = convertDateTime(dl.createDateUtc);

    dl.reportName = ["complete", "stale", "vintage", "preview"].includes(dl.reportStatus as string) ?
      <>{makeDownloadButton(dl.projectId, dl.id, dl.reportName as string)}</> :
      dl.reportName;
    dl.reportStatusEnum = ReportStatusConverter[dl.reportStatus as keyof typeof ReportStatusConverter];
    dl.reportStatus = <>{reportStatusIcons[dl.reportStatusEnum as string] || <i className="fa fa-circle-o-notch fa-spin" />} {dl.reportStatusEnum}</>;
    dl.includeActiveRegionSets = formatBoolean(dl.includeActiveRegionSets as boolean);
    dl.includeApprovedRegionSets = formatBoolean(dl.includeApprovedRegionSets as boolean);
    dl.includeFacilitySpecialties = formatBoolean(dl.includeFacilitySpecialties as boolean);
    dl.includePendingRegionSets = formatBoolean(dl.includePendingRegionSets as boolean);
    dl.includePhysicianSpecialties = formatBoolean(dl.includePhysicianSpecialties as boolean);
    dl.isHsd = formatBoolean(dl.isHsd as boolean);
    dl.analysisType = AnalysisTypeConverter[dl.analysisType as keyof typeof AnalysisTypeConverter];
    dl.providerCategory = ProviderCategoryConverter[dl.providerCategory as keyof typeof ProviderCategoryConverter];
    dl.specialtyFilter = SpecialtyFilterConverter[dl.specialtyFilter as keyof typeof SpecialtyFilterConverter];
    dl.exportType = ExportTypeConverter[dl.exportType as keyof typeof ExportTypeConverter];
    dl.serviceAreaType = ServiceAreaTypeConverter[dl.serviceAreaType as keyof typeof ServiceAreaTypeConverter];
    dl.fileType = ReportFileTypeConverter[dl.fileType as keyof typeof ReportFileTypeConverter];
    dl.projectType = ProjectTypeConverter[dl.projectType as keyof typeof ProjectTypeConverter];
    dl.asOfUtc = asOfTimestamp.toLocaleString();
    dl.createDateUtc = lastRequestTimestamp.toLocaleString();
    return dl;
  }
  const getSearchValue = (isSecondValue?: boolean) => {
    let queriedValue = isSecondValue ? searchValue2 : searchValue;
    if (searchType === "As Of" || searchType === "Last Requested") {
      const searchDate = new Date(queriedValue);
      // Undo the timezone shifting we did
      searchDate.setMinutes(searchDate.getMinutes() + searchDate.getTimezoneOffset());
      // Set the previous midnigt for start date, next midnight for end date
      searchDate.setHours(isSecondValue ? 24 : 0, 0, 0, 0);
      // And convert to UTC for the database
      queriedValue = searchDate.toUTCString();
    }
    return queriedValue;
  }

  const fetchToday = async () => {
    const startDate = new Date();
    startDate.setHours(0, 0, 0, 0);

    const endDate = new Date();
    endDate.setHours(24, 0, 0, 0);

    const response = await exportService.getAvailableReports(startDate.toUTCString(), endDate.toUTCString(), searchType, getSearchValue(), getSearchValue(true));
    setReportsToday(response.map(fixResponse));
    setHasLoadedToday(true);
  }

  const fetchLast7 = async () => {
    const sevenDaysAgo = getDate(7);
    sevenDaysAgo.setHours(0, 0, 0, 0);

    const yesterday = getDate(1);
    yesterday.setHours(24, 0, 0, 0);

    const response = await exportService.getAvailableReports(sevenDaysAgo.toUTCString(), yesterday.toUTCString(), searchType, getSearchValue(), getSearchValue(true));
    setReportsLast7(response.map(fixResponse));
    setHasLoadedLast7(true);
  }

  const fetchLast30 = async () => {
    const thirtyDaysAgo = getDate(30);
    thirtyDaysAgo.setHours(0, 0, 0, 0);

    const eightDaysAgo = getDate(8);
    eightDaysAgo.setHours(24, 0, 0, 0);

    const response = await exportService.getAvailableReports(thirtyDaysAgo.toUTCString(), eightDaysAgo.toUTCString(), searchType, getSearchValue(), getSearchValue(true));
    setReportsLast30(response.map(fixResponse));
    setHasLoadedLast30(true);
  }

  const fetchSharedToday = async () => {
    const startDate = new Date();
    startDate.setHours(0, 0, 0, 0);

    const endDate = new Date();
    endDate.setHours(24, 0, 0, 0);

    const response = await exportService.getAvailableSharedReports(startDate.toUTCString(), endDate.toUTCString(), searchType, getSearchValue(), getSearchValue(true));
    setSharedReportsToday(response.map(fixResponse));
    setHasLoadedSharedToday(true);
  }

  const fetchSharedLast7 = async () => {
    const sevenDaysAgo = getDate(7);
    sevenDaysAgo.setHours(0, 0, 0, 0);

    const yesterday = getDate(1);
    yesterday.setHours(24, 0, 0, 0);

    const response = await exportService.getAvailableSharedReports(sevenDaysAgo.toUTCString(), yesterday.toUTCString(), searchType, getSearchValue(), getSearchValue(true));
    setSharedReportsLast7(response.map(fixResponse));
    setHasLoadedSharedLast7(true);
  }

  const fetchSharedLast30 = async () => {
    const thirtyDaysAgo = getDate(30);
    thirtyDaysAgo.setHours(0, 0, 0, 0);

    const eightDaysAgo = getDate(8);
    eightDaysAgo.setHours(24, 0, 0, 0);

    const response = await exportService.getAvailableSharedReports(thirtyDaysAgo.toUTCString(), eightDaysAgo.toUTCString(), searchType, getSearchValue(), getSearchValue(true));
    setSharedReportsLast30(response.map(fixResponse));
    setHasLoadedSharedLast30(true);
  }

  const getDate = (daysAgo: number) => {
    return new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000);
  }

  const handleSearchTypeChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setSearchType(e.target.value);
    setSearchValue("");
    setSearchValue2("");
  }

  const handleSearch = () => {
    setSearching(true);
    Promise.all([
      fetchToday(),
      fetchLast7(),
      fetchLast30(),
      fetchSharedToday(),
      fetchSharedLast7(),
      fetchSharedLast30()
    ]).then(() => {
      setSearching(false);
      setHasLoadedLast7(true);
      setHasLoadedLast30(true);
      setHasLoadedSharedToday(true);
      setHasLoadedSharedLast7(true);
      setHasLoadedSharedLast30(true);
    }, () => {
      setSearching(false);
      setHasLoadedLast7(true);
      setHasLoadedLast30(true);
      setHasLoadedSharedToday(true);
      setHasLoadedSharedLast7(true);
      setHasLoadedSharedLast30(true);
    });
  }

  return (
    <ReportsContext.Provider
      value={{
        availableFilterCriteria,
        downloadFile,
        fetchLast7,
        fetchLast30,
        fetchSharedLast7,
        fetchSharedLast30,
        fetchSharedToday,
        fetchToday,
        handleSearch,
        handleSearchTypeChange,
        hasLoadedLast7,
        hasLoadedLast30,
        hasLoadedSharedLast7,
        hasLoadedSharedLast30,
        hasLoadedSharedToday,
        hasLoadedToday,
        reportsLast7,
        reportsLast30,
        reportsToday,
        searchType,
        searchValue,
        searchValue2,
        searching,
        setSearchType,
        setSearchValue,
        setSearchValue2,
        sharedReportsLast7,
        sharedReportsLast30,
        sharedReportsToday
      }}
    >
      {children}
    </ReportsContext.Provider>
  );
};
